import { Component, Input, OnInit } from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { ACLService } from "@delon/acl";
import { CacheService } from "@delon/cache";
import { TranslateService } from "@root/shared/service/translate.service";

@Component({
  selector: "v4-common-order-list-filter",
  templateUrl: "./order-list-filter.component.html",
  styleUrls: ["./order-list-filter.component.scss"],
})
export class V4CommonOrderListFilterComponent implements OnInit {
  @Input() form: UntypedFormGroup;
  @Input() datePickerType: "simple" | "detail" = "simple";
  @Input() showAccountPicker = true;
  readonly optionsOfDateType = [
    { i18nKey: "order.check_in_time", value: "check_in", label: "" },
    { i18nKey: "order.check_out_time", value: "check_out", label: "" },
    { i18nKey: "order.booked_time_time_1", value: "booked_time", label: "" },
    { i18nKey: "order.cancel_time_time_1", value: "cancel_time", label: "" },
  ];
  readonly optionsOfSettlementStatus = [
    { i18nKey: "word.any", value: null, label: "" },
    { i18nKey: "order.owing", value: 1, label: "" },
    { i18nKey: "order.money_cleared", value: 0, label: "" },
  ];
  readonly optionsOfHourReser = [
    { i18nKey: "word.any", value: null, label: "" },
    { i18nKey: "word.select_option.only_hour_res", value: 1, label: "" },
    { i18nKey: "word.select_option.exclude_hour_res", value: 2, label: "" },
  ];
  readonly optionsOfExtReser = [
    { i18nKey: "reservation.list.filters.filter_ext_res.opt_1", value: null, label: "" },
    { i18nKey: "reservation.list.filters.filter_ext_res.opt_2", value: 1, label: "" },
    { i18nKey: "reservation.list.filters.filter_ext_res.opt_3", value: 2, label: "" },
  ];
  readonly optionsOfReserBox = [
    { i18nKey: "word.any", value: null, label: "" },
    { i18nKey: "word.select_option.only_res_box", value: 1, label: "" },
    { i18nKey: "word.select_option.exclude_res_box", value: 2, label: "" },
  ];
  readonly optionsOfLabel = [
    { value: "conflict", label: "reservation.list.filter.label.option_c" },
    { value: "handmade", label: "reservation.list.filter.label.option_h" },
  ];
  optionsOfChannel = [];
  optionsOfAccount = [];
  optionsOfOperator = [];
  optionsOfTag = [];
  optionsOfCreator = [];

  readonly optionsOfFilter = [
    { i18nKey: "word.select_option.placeholder.house", value: "house_ids" },
    { i18nKey: "word.select_option.placeholder.room_type", value: "room_type_ids" },
    { i18nKey: "word.select_option.placeholder.channel", value: "channels" },
    { i18nKey: "word.select_option.placeholder.account", value: "thirdparty_account_ids" },
    { i18nKey: "reservation.list.filters.filter_sub_account.placeholder", value: "operator_ids" },
    { i18nKey: "order.house_fee", value: "house_fee_range" },
    { i18nKey: "word.select_option.placeholder.settlement_status", value: "settlement_status" },
    { i18nKey: "reservation.list.filters.filter_tag.placeholder", value: "tags" },
    { i18nKey: "reservation.list.filters.filter_label.placeholder", value: "labels" },
    { i18nKey: "word.select_option.placeholder.hour_reservation", value: "hour_reservation" },
    { i18nKey: "reservation.list.filters.filter_ext_res.placeholder", value: "extension_reservation" },
    { i18nKey: "word.select_option.placeholder.res_box", value: "reservation_box" },
    { i18nKey: "reservation.list.filters.filter_creator.placeholder", value: "creator_operator_id" },
  ];

  filters = (this.cache.getNone("order_list_filter") as Array<any>) || [
    "house_ids",
    "channels",
    "thirdparty_account_ids",
  ];

  get isSubaccount() {
    return this.acl.can({ role: ["slave"] });
  }

  constructor(
    private cache: CacheService,
    private t: TranslateService,
    private acl: ACLService,
  ) {}

  ngOnInit() {
    this.initFilterOptions();
  }

  private initFilterOptions() {
    this.initTagAndChannelOptions();
    this.initAccountOptions();
    this.initCreatorOptions();
    if (!this.isSubaccount) {
      this.initSubaccountOptions();
    }
  }

  private initTagAndChannelOptions() {
    this.cache
      .get("/api/house_status_setting/get", { mode: "promise", type: "m", expire: 10 })
      .subscribe((res: any) => {
        if (res.error_code === 0) {
          const tagList = res?.data?.tag_list;
          const channelList = res?.data?.reservation_channel;

          this.optionsOfTag = tagList
            .filter((tag) => !tag.delete_time)
            .map((t) => ({ label: t.tag_name, value: t.id }));
          this.optionsOfChannel = channelList
            .filter((channel) => !channel.delete_time)
            .map((c) => ({ label: c.channel_name, value: c.code }));
        }
      });
  }

  private initAccountOptions() {
    const channels = this.form.get("channels").value;

    const thirdpartyName = (type) => this.t.t("word.channel_" + type);

    this.cache
      .get(`/api/reservation/thirdparty_list?thirdparty_types=${JSON.stringify(channels || [])}`, {
        mode: "promise",
        type: "m",
        expire: 10,
      })
      .subscribe((res: any) => {
        if (res.error_code === 0) {
          const accounts = res?.data?.list;
          const options = accounts.map((a) => ({
            label: `${thirdpartyName(a.thirdparty_type)}-${a.username}`,
            value: a.id,
          }));
          this.optionsOfAccount = [
            {
              label: this.t.t("word.any"),
              value: null,
            },
          ].concat(options);
        }
      });
  }

  private initCreatorOptions() {
    this.cache
      .get("/api/sub_account/list?page=1&page_size=1000&format=reservation_creator", {
        mode: "promise",
        type: "m",
        expire: 10,
      })
      .subscribe((rst: any) => {
        const list = rst?.data?.list;
        this.optionsOfCreator = list.map((account) => ({
          label: account.creator_name,
          value: account.operator_id,
        }));
      });
  }

  private initSubaccountOptions() {
    this.cache
      .get("/api/sub_account/list?page=1&page_size=1000&format=reservation_filter", {
        mode: "promise",
        type: "m",
        expire: 10,
      })
      .subscribe((rst: any) => {
        const list = rst?.data?.list || [];
        this.optionsOfOperator = list.map((a) => ({
          label: a.creator_name,
          value: a.operator_id,
        }));
      });
  }

  onFilterChange(value) {
    const key = "order_list_filter";
    this.cache.set(key, value);
  }
}
